import is from '@sindresorhus/is';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../context/context';
import type { Ambition, AmbitionOptions, Ambitions, UserAmbitionUpdatePayload } from '../data-contract/ambition';
import { ambitionOptionsSchema, ambitionSchema, ambitionsSchema } from '../data-contract/ambition';
import type { Pathname } from '../http-fetcher/http-fetcher';

export const useUserAmbitionsQuery = (userId: string, enabled: boolean = true): UseQueryResult<Ambitions, Error> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/ambitions`;

    return useQuery({
        enabled: enabled && is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(ambitionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', applicationState.languageKey],
    });
};

export const useUserAmbitionOptionsQuery = (userId: string): UseQueryResult<AmbitionOptions, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/ambitions/options`;

    return useQuery({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(ambitionOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

export type AmbitionUseMutationResult = UseMutationResult<unknown, Error, readonly Ambition[]>;

export const useStoreUserAmbitions = (userId: string): AmbitionUseMutationResult => {
    const { applicationState, cobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();
    const pathname: Pathname = `/users/${userId}/ambitions`;

    return useMutation({
        mutationFn: async (ambitions) => {
            const result = await cobrainerApiFetcher.postJson({
                pathname,
                payload: ambitions.map(({ id }) => ({ id })),
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [pathname, 'postJson'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [pathname, 'get', applicationState.languageKey] });
        },
    });
};

export const useUpdateUserAmbitions = (
    userId: string,
): UseMutationResult<unknown, Error, UserAmbitionUpdatePayload> => {
    const { applicationState, cobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/ambitions`;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const result = await cobrainerApiFetcher.patchJson({
                pathname,
                payload,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [pathname, 'patchJson'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [pathname, 'GET', applicationState.languageKey] });
        },
    });
};

export const useAmbitionSuggestionsQuery = ({
    ambitions,
    enabled,
    hardSkillsLimit = 10,
    languageSkillsLimit = 2,
    softSkillsLimit = 10,
    userId,
}: {
    readonly ambitions?: readonly Ambition[];
    readonly enabled: boolean;
    readonly hardSkillsLimit?: number;
    readonly languageSkillsLimit?: number;
    readonly softSkillsLimit?: number;
    readonly userId: string;
}): UseQueryResult<readonly Ambition[]> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/ambitions/suggestions`;

    const ambitionsPayload = ambitions?.map(({ id }) => ({
        id,
    }));

    return useQuery({
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(ambitionSchema.array(), {
                pathname,
                payload: {
                    ambitions: ambitionsPayload ?? [],
                    hardSkillsLimit,
                    languageSkillsLimit,
                    softSkillsLimit,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'postJson', ambitions, hardSkillsLimit, languageSkillsLimit, softSkillsLimit],
    });
};
